import {
  EntityQRCodeParametersCreateBody,
  EntityQRCodeParametersUpdateBody,
  QRCodeDestinationType,
  QRCodeSaveParameters,
  QRCodeType,
} from '../types';
import { getQrData, mapDestinationTypeToQRCodeType } from './helpers';

export type QRCodeStoredDestination = { type: QRCodeType; data: string };

export type QRCodeWriteInput = {
  name: string;
  formData: QRCodeSaveParameters;
  destinationType: QRCodeDestinationType;
  storedDestination: QRCodeStoredDestination | null;
  // The keyword the code is saved for, so that the record it creates keeps the keyword's row.
  keywordId: string | null;
};

/**
 * A stored code keeps the data it was stored with. The form works in destination types, and what it
 * holds is not a faithful copy of the code: Compose Email has no destination type of its own, comes
 * back as a URL, and rebuilding the data from the form would write it back as a website.
 */
export function qrCodeWriteData(
  formData: QRCodeSaveParameters,
  destinationType: QRCodeDestinationType,
  storedDestination: QRCodeStoredDestination | null,
): string {
  return storedDestination?.data ?? getQrData(formData, destinationType);
}

// The destination is left out on purpose: the backend refuses an update that carries one.
export function buildQRCodeUpdateBody(
  { name, formData }: Pick<QRCodeWriteInput, 'name' | 'formData'>,
): EntityQRCodeParametersUpdateBody {
  return {
    parameters: writeParameters(formData),
    name: name,
  };
}

export function buildQRCodeCreateBody(input: QRCodeWriteInput): EntityQRCodeParametersCreateBody {
  const { name, formData, destinationType, storedDestination, keywordId } = input;
  return {
    type: storedDestination?.type ?? mapDestinationTypeToQRCodeType(destinationType),
    keywordId: keywordId,
    text: name,
    data: qrCodeWriteData(formData, destinationType, storedDestination),
    parameters: writeParameters(formData),
    name: name,
  };
}

function writeParameters(formData: QRCodeSaveParameters): string {
  return JSON.stringify({
    styling: formData.styling,
    destinationData: formData.destinationData,
    previewFormat: formData.previewFormat,
    previewSize: formData.previewSize,
  } satisfies QRCodeSaveParameters);
}
